import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"
import { Slot } from "radix-ui"

const emptyVariants = cva(
  "group/empty flex min-w-0 flex-1 flex-col items-center justify-center text-center text-balance",
  {
    variants: {
      variant: {
        default: "",
        outline: "rounded-lg border border-dashed border-border",
      },
      size: {
        sm: "gap-4 p-6",
        md: "gap-6 p-8 md:p-12",
        lg: "gap-6 p-12 md:p-16",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
    },
  },
)

function Empty({
  className,
  variant = "default",
  size = "md",
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof emptyVariants>) {
  return (
    <div
      data-slot="empty"
      data-size={size}
      className={cn(emptyVariants({ variant, size }), className)}
      {...props}
    />
  )
}

function EmptyHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="empty-header"
      className={cn(
        "flex max-w-sm flex-col items-center gap-1.5 text-center",
        className,
      )}
      {...props}
    />
  )
}

const emptyMediaVariants = cva(
  "mb-2 flex shrink-0 items-center justify-center group-data-[size=sm]/empty:mb-1 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-transparent",
        icon: "size-10 rounded-md border border-border bg-muted text-muted-foreground group-data-[size=sm]/empty:size-8 [&_svg:not([class*='size-'])]:size-5 group-data-[size=sm]/empty:[&_svg:not([class*='size-'])]:size-4",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
)

function EmptyMedia({
  className,
  variant = "default",
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof emptyMediaVariants>) {
  return (
    <div
      data-slot="empty-icon"
      data-variant={variant}
      aria-hidden="true"
      className={cn(emptyMediaVariants({ variant, className }))}
      {...props}
    />
  )
}

/**
 * An h3 by default, so the page outline lists it. Use `asChild` to render the
 * level the page needs, for example `<EmptyTitle asChild><h2>…</h2></EmptyTitle>`.
 */
function EmptyTitle({
  asChild = false,
  className,
  ...props
}: React.ComponentProps<"h3"> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : "h3"
  return (
    <Comp
      data-slot="empty-title"
      className={cn(
        "font-display text-h3 text-foreground group-data-[size=sm]/empty:font-sans group-data-[size=sm]/empty:text-body-sm group-data-[size=sm]/empty:font-medium",
        className,
      )}
      {...props}
    />
  )
}

function EmptyDescription({ className, ...props }: React.ComponentProps<"p">) {
  return (
    <p
      data-slot="empty-description"
      className={cn(
        "text-body-sm text-muted-foreground group-data-[size=sm]/empty:text-ui [&>a]:text-foreground [&>a]:underline [&>a]:underline-offset-4 [&>a]:focus-ring",
        className,
      )}
      {...props}
    />
  )
}

function EmptyContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="empty-content"
      className={cn(
        "flex w-full max-w-sm min-w-0 flex-col items-center gap-3 text-ui text-balance",
        className,
      )}
      {...props}
    />
  )
}

export {
  Empty,
  EmptyHeader,
  EmptyTitle,
  EmptyDescription,
  EmptyContent,
  EmptyMedia,
  emptyVariants,
}
